import React, { useState } from 'react';
import { SquarePen, Wrench, Rotate3d, MountainSnow, Upload, ChartArea, Printer } from 'lucide-react'; // ChevronDown, ChevronUp rimosse, CloudSun aggiunta, Printer aggiunta
import useGeoData, { selectFullLayerData } from '../../store/useGeoData';
import type { RightPanelTabId } from '../../store/types';
import './RightPanelContainer.css';
import LayerOptionsPanel from './components/LayerOptionsPanel';
import { Tabs } from '../../common/Tabs'; // Importa il nostro componente Tabs
import type { TabItem } from '../../common/Tabs'; // Importa il tipo TabItem
import ManagePopupFieldsDialog from './components/ManagePopupFieldsDialog'; // Importa il nuovo dialog
import TerrainPanel from './components/TerrainPanel'; // Aggiunto import
import { LazyContoursPanel } from '../../common/LazyFeatureLoader'; // Lazy import del pannello Contours
import ImportPanel from '../import/components/ImportPanel'; // Importa il nuovo pannello Import
import { HierarchicalAccordion } from '../../common/HierarchicalAccordion'; // Importa Accordion
import { HierarchicalAccordionItem } from '../../common/HierarchicalAccordionItem'; // Importa AccordionItem
import ChartsPanel from '../../features/charts/components/ChartsPanel';
import PrintMapsPanel from '../print/components/PrintMapsPanel'; // Importa il nuovo pannello Print

interface RightPanelContainerProps {
  isChartsDisplayVisible: boolean;
  onToggleChartsDisplay: () => void;
}

const RightPanelContainer: React.FC<RightPanelContainerProps> = ({
  isChartsDisplayVisible,
  onToggleChartsDisplay,
}) => {
  // --- State Selection ---
  const activeTab = useGeoData((state) => state.activeRightPanelTab);
  const setActiveTab = useGeoData((state) => state.setActiveRightPanelTab);
  const selectedLayerId = useGeoData((state) => state.selectedLayerId);
  // const getLayerById = useGeoData((state) => state.getLayerById); // Necessario per DataActionsPanel? Verificare
  const updateLayerStyleProperty = useGeoData((state) => state.updateLayerStyleProperty);
  const openAttributeTable = useGeoData((state) => state.openAttributeTable);
  // const layerStyles = useGeoData((state) => state.layerStyles); // Necessario per isPopupEnabled
  const togglePopupEnabled = useGeoData((state) => state.togglePopupEnabled); // Ora dovrebbe essere riconosciuto
  // Recupera lo stato popup specifico per il layer selezionato
  const isPopupEnabled = useGeoData((state) =>
  selectedLayerId ? state.layerStyles[selectedLayerId]?.isPopupEnabled ?? false : false // Aggiunto fallback a false
);
// Recupera gli ID delle tabelle attributi aperte
const openAttributeTableIds = useGeoData((state) => state.openAttributeTableIds);
const setPopupFields = useGeoData((state) => state.setPopupFields); // Importa l'azione per salvare

// Selettore per dati completi del layer
const fullLayerData = useGeoData(state => selectedLayerId ? selectFullLayerData(state, selectedLayerId) : null) ?? null;

// Stato per la visibilità del dialog
const [isManageFieldsDialogOpen, setIsManageFieldsDialogOpen] = useState(false);

// Stato per gestire l'accordion nel pannello destro (scheda Tools) - RIMOSSO activeMenuId
// const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
// const [isAddRasterDialogOpen, setIsAddRasterDialogOpen] = useState(false); // Rimosso: gestito in ImportPanel
// const [isAddVectorDialogOpen, setIsAddVectorDialogOpen] = useState(false); // Rimosso: gestito in ImportPanel

  // --- Helper Constants ---
  const selectedTabIndex = activeTab === 'data' ? 0 : activeTab === 'style' ? 1 : 0; // Default a 0 se activeTab è null

  // --- Event Handlers ---
  const handleTabChange = (index: number) => {
    const newTabId = index === 0 ? 'data' : 'style';
    setActiveTab(newTabId);
  };

  const handleOpenManageFieldsDialog = () => {
    setIsManageFieldsDialogOpen(true);
  };

  const handleCloseManageFieldsDialog = () => {
    setIsManageFieldsDialogOpen(false);
  };

  const handleSaveManageFields = (fields: string[] | null) => {
    if (selectedLayerId) {
      setPopupFields(selectedLayerId, fields);
    }
    handleCloseManageFieldsDialog(); // Chiude il dialog dopo il salvataggio
  };

  // handleMouseEnter e handleMouseLeave RIMOSSE
  // const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
  //   e.currentTarget.style.backgroundColor = 'var(--color-background-ui-hover)';
  // };

  // const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
  //   e.currentTarget.style.backgroundColor = 'transparent';
  // };

  // --- Render Functions ---
  const renderTabContent = (tabId: RightPanelTabId) => {
    switch (tabId) {
      // case 'data': // Rimosso perchè il componente DataActionsPanel è stato eliminato
      //   return <DataActionsPanel selectedLayerId={selectedLayerId} />;
      case 'style':
        // LayerOptionsPanel gestisce internamente il messaggio se nessun layer è selezionato
        return (
          <LayerOptionsPanel
            layerData={fullLayerData}
            updateLayerStyle={updateLayerStyleProperty}
            openAttributeTable={openAttributeTable}
            togglePopupEnabled={togglePopupEnabled} // Passa la funzione
            openManageFieldsDialog={handleOpenManageFieldsDialog} // Passa la funzione
            isPopupEnabled={isPopupEnabled} // Passa lo stato
            isAttributeTableOpen={selectedLayerId ? openAttributeTableIds.includes(selectedLayerId) : false} // Passa lo stato della tabella attributi
            isStyleEditorDisabled={!selectedLayerId}
          />
        );
      default:
        // Questo caso non dovrebbe verificarsi se una scheda è selezionata
        return <div className="placeholder-message">Select a tab.</div>;
    }
  };

  // --- Preparazione Items per il nuovo componente Tabs ---
  const toolsPanelContent = (
    <div className="right-panel-container__content">
      <HierarchicalAccordion defaultOpenId={null}>
        <HierarchicalAccordionItem
          id="load-data"
          title="Load Data"
          level={1}
          icon={Upload}
        >
          <ImportPanel />
          {/* I pulsanti per aggiungere layer da URL sono stati spostati in ImportPanel */}
        </HierarchicalAccordionItem>

        <HierarchicalAccordionItem
          id="3d-scene"
          title="3D Scene"
          level={1}
          icon={Rotate3d}
        >
          <TerrainPanel />
        </HierarchicalAccordionItem>

        <HierarchicalAccordionItem
          id="extract-contours"
          title="Contours"
          level={1}
          icon={MountainSnow}
        >
          <LazyContoursPanel />
        </HierarchicalAccordionItem>

        <HierarchicalAccordionItem
          id="charts"
          title="Charts"
          level={1}
          icon={ChartArea}
        >
          <ChartsPanel
            isChartsDisplayVisible={isChartsDisplayVisible}
            onToggleChartsDisplay={onToggleChartsDisplay}
          />
        </HierarchicalAccordionItem>

        <HierarchicalAccordionItem
          id="print-maps"
          title="Print Map"
          level={1}
          icon={Printer}
        >
          <PrintMapsPanel />
        </HierarchicalAccordionItem>

      </HierarchicalAccordion>
    </div>
  );

  const layerSettingsPanelContent = (
    <div className="right-panel-container__content">
      {renderTabContent('style')}
      {!selectedLayerId && (
        <div className="placeholder-message">
          Select a layer from the list to edit its style.
        </div>
      )}
    </div>
  );


  const tabItems: TabItem[] = [
    {
      label: 'Tools',
      icon: <Wrench size={16} />,
      content: toolsPanelContent,
    },
    {
      label: 'Layer Settings',
      icon: <SquarePen size={16} />,
      content: layerSettingsPanelContent,
      disabled: !selectedLayerId && activeTab !== 'style', // Disabilita se nessun layer è selezionato e non siamo già su style
    },
  ];

  // --- Component Return ---
  return (
    <div className="right-panel-container">
      <Tabs
        tabs={tabItems}
        defaultActiveTabIndex={selectedTabIndex}
        onTabChange={handleTabChange} // Passa la callback per gestire il cambio di tab
      />
      {/* Renderizza il dialog modale */}
      {selectedLayerId && fullLayerData && (
        <ManagePopupFieldsDialog
          isOpen={isManageFieldsDialogOpen}
          onClose={handleCloseManageFieldsDialog}
          layerId={selectedLayerId}
          layerName={fullLayerData.name} // Corretto accesso alla proprietà
          availableFields={fullLayerData.availablePropertyKeys || []} // Corretto accesso alla proprietà
          initialSelectedFields={fullLayerData.popupFields ?? null} // Aggiunto fallback per undefined
          onSave={handleSaveManageFields}
        />
      )}

      {/* I dialoghi AddRasterLayerDialog e AddVectorLayerDialog sono stati spostati in ImportPanel */}
    </div>
  );
};

export default RightPanelContainer;